import React, { useMemo, useState } from 'react';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import { fmtVal, fmtDelta, fmtPctDelta } from './formatters';
import './PerformanceSummary.css';

/**
 * PerformanceSummary — refined card-style breakdown table used by each
 * dashboard tab (Revenue / Operational / Financial).
 *
 * Props:
 *  - title:    string
 *  - subtitle: string (optional)
 *  - columns:  [{ key, label, fmt, inverted?, hideDelta?, align? }]
 *  - rows:     [{ id, flag, name, rowTone, isTotal?, cells: { [key]: { value, delta, deltaPct, tone, tooltip } } }]
 *  - viewMode: 'value' | 'pct'  (controlled)
 *  - onViewModeChange(mode)
 *  - expandable, expanded, onExpand
 *  - onExport
 *  - initialSort: { key, dir }
 *  - emptyMessage: string
 */
function PerformanceSummary({
  title,
  subtitle,
  columns = [],
  rows = [],
  viewMode = 'value',
  onViewModeChange,
  expandable = false,
  expanded = false,
  onExpand,
  onExport,
  initialSort = null,
  emptyMessage = 'No data for the current selection.',
  cellHover, // optional: { onEnter(payload, e), onMove(payload, e), onLeave() }
  currency,  // ISO-4217. NULL → USD via shared helper.
  variant,   // 'report' renders with the ReportsNative table kit (rn-card / rn-detail-table)
}) {
  const [sort, setSort] = useState(initialSort);

  const onSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return null;
    });
  };

  // Separate data rows from the totals row; totals are pinned in <tfoot>.
  const bodyRows = useMemo(() => rows.filter((r) => !r.isTotal), [rows]);
  const totalRow = useMemo(() => rows.find((r) => r.isTotal) || null, [rows]);

  const sortedRows = useMemo(() => {
    if (!sort) return bodyRows;
    const { key, dir } = sort;
    const copy = [...bodyRows];
    copy.sort((a, b) => {
      const av = a.cells?.[key]?.value;
      const bv = b.cells?.[key]?.value;
      const an = typeof av === 'number' ? av : Number.NEGATIVE_INFINITY;
      const bn = typeof bv === 'number' ? bv : Number.NEGATIVE_INFINITY;
      return dir === 'desc' ? bn - an : an - bn;
    });
    return copy;
  }, [bodyRows, sort]);

  const renderCellContents = (cell, col, { strong = false } = {}) => {
    if (!cell) return <div className="prop-val muted">—</div>;
    const { value, delta, deltaPct, tone } = cell;
    const showDelta = !col.hideDelta && delta != null && delta !== 0;
    return (
      <>
        <div className={'prop-val' + (strong ? ' strong' : '')}>
          {fmtVal(value, col.fmt, currency)}
        </div>
        {showDelta && (
          <div className={'prop-delta tone-' + (tone || 'neu')}>
            <span className="prop-delta-arrow" aria-hidden="true">
              {delta > 0 ? '▲' : '▼'}
            </span>
            {viewMode === 'value'
              ? fmtDelta(delta, col.fmt, currency)
              : fmtPctDelta(deltaPct)}
          </div>
        )}
      </>
    );
  };

  // Build hover events for a numeric cell. Uses the cell's `tooltipPayload`
  // so the dashboard can render a custom floating tooltip via portal.
  const cellHoverEvents = (cell) => {
    if (!cellHover || !cell?.tooltipPayload) return null;
    return {
      onMouseEnter: (e) => cellHover.onEnter?.(cell.tooltipPayload, e),
      onMouseMove:  (e) => cellHover.onMove?.(cell.tooltipPayload, e),
      onMouseLeave: () => cellHover.onLeave?.(),
      onFocus:      (e) => cellHover.onEnter?.(cell.tooltipPayload, e),
      onBlur:       () => cellHover.onLeave?.(),
    };
  };

  const hasRows = sortedRows.length > 0;

  if (variant === 'report') {
    return (
      <ReportVariant
        title={title}
        subtitle={subtitle}
        columns={columns}
        sortedRows={sortedRows}
        totalRow={totalRow}
        sort={sort}
        onSort={onSort}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        expandable={expandable}
        expanded={expanded}
        onExpand={onExpand}
        onExport={onExport}
        emptyMessage={emptyMessage}
        cellHoverEvents={cellHoverEvents}
        currency={currency}
      />
    );
  }

  return (
    <div className={'perf-summary' + (expanded ? ' expanded' : '')}>
      <div className="perf-summary-head">
        <div>
          <h2 className="perf-summary-title">{title}</h2>
          {subtitle && <div className="perf-summary-sub">{subtitle}</div>}
        </div>
        <div className="perf-summary-controls">
          {onViewModeChange && (
            <div className="ps-toggle" role="tablist" aria-label="Cell view">
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === 'value'}
                className={'ps-toggle-btn' + (viewMode === 'value' ? ' active' : '')}
                onClick={() => onViewModeChange('value')}
              >
                $ / rooms
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === 'pct'}
                className={'ps-toggle-btn' + (viewMode === 'pct' ? ' active' : '')}
                onClick={() => onViewModeChange('pct')}
              >
                % change
              </button>
            </div>
          )}
          {expandable && (
            <button
              type="button"
              className="ps-btn"
              onClick={onExpand}
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
              {expanded ? 'Collapse' : 'Expand'}
            </button>
          )}
          {onExport && (
            <button
              type="button"
              className="ps-btn"
              onClick={onExport}
              title="Export to Excel"
            >
              <Download size={12} />
              Export
            </button>
          )}
        </div>
      </div>

      {hasRows ? (
        <div className="perf-summary-scroll">
        <table className="perf-summary-table">
          <thead>
            <tr>
              <th className="first">Property</th>
              {columns.map((col) => {
                const isSorted = sort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    className={'sortable' + (isSorted ? ' sorted' : '')}
                    onClick={() => onSort(col.key)}
                  >
                    {col.label}
                    <span className="ps-sort">
                      {isSorted ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr
                key={row.id}
                className={'perf-row tone-' + (row.rowTone || 'neu')}
              >
                <td className="first">
                  <div className="prop-cell">
                    <span className={'prop-flag tone-' + (row.rowTone || 'neu')}>
                      {row.flag}
                    </span>
                    <span className="prop-name">{row.name}</span>
                  </div>
                </td>
                {columns.map((col) => {
                  const cell = row.cells?.[col.key];
                  return (
                    <td key={col.key} {...(cellHoverEvents(cell) || {})}>
                      {renderCellContents(cell, col)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {totalRow && (
            <tfoot>
              <tr>
                <td className="first">{totalRow.name || 'TOTAL'}</td>
                {columns.map((col) => {
                  const cell = totalRow.cells?.[col.key];
                  return (
                    <td key={col.key} {...(cellHoverEvents(cell) || {})}>
                      {renderCellContents(cell, col, { strong: true })}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
        </div>
      ) : (
        <div className="perf-summary-empty">{emptyMessage}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Report-kit rendering: the same card / toolbar / detail-table grammar */
/* as the native report pages, so dashboard tables match report tables. */
/* Used by the New-layout dashboard only.                               */
/* ------------------------------------------------------------------ */
const VAR_TONE = { pos: 'rn-var-pos', neg: 'rn-var-neg', warn: 'rn-var-neg', neu: 'rn-var-zero' };

function ReportVariant({
  title, subtitle, columns, sortedRows, totalRow, sort, onSort, viewMode, onViewModeChange,
  expandable, expanded, onExpand, onExport, emptyMessage, cellHoverEvents, currency,
}) {
  const hasRows = sortedRows.length > 0;
  const renderCell = (cell, col) => {
    if (!cell) return <span className="rn-muted">—</span>;
    const { value, delta, deltaPct, tone } = cell;
    const showDelta = !col.hideDelta && delta != null && delta !== 0;
    return (
      <>
        <div>{fmtVal(value, col.fmt, currency)}</div>
        {showDelta && (
          <div className="rn-perf-delta">
            <span className={'rn-var ' + (VAR_TONE[tone] || VAR_TONE.neu)}>
              {viewMode === 'value' ? fmtDelta(delta, col.fmt, currency) : fmtPctDelta(deltaPct)}
            </span>
          </div>
        )}
      </>
    );
  };
  return (
    <section className={'rn-card rn-perf' + (expanded ? ' rn-perf-expanded' : '')}>
      <div className="rn-table-toolbar">
        <h2>{title}</h2>
        {subtitle && <span className="rn-pill-note">{subtitle}</span>}
        <div className="rn-spacer" />
        {onViewModeChange && (
          <div className="rn-seg" role="tablist" aria-label="Cell view">
            <button type="button" role="tab" aria-selected={viewMode === 'value'} className={viewMode === 'value' ? 'rn-active' : ''} onClick={() => onViewModeChange('value')}>
              $ / rooms
            </button>
            <button type="button" role="tab" aria-selected={viewMode === 'pct'} className={viewMode === 'pct' ? 'rn-active' : ''} onClick={() => onViewModeChange('pct')}>
              % change
            </button>
          </div>
        )}
        {(expandable || onExport) && (
          <div className="rn-tools">
            {onExport && (
              <button type="button" className="rn-icon-btn" title="Export to Excel" onClick={onExport}>
                <Download />
              </button>
            )}
            {expandable && (
              <button type="button" className="rn-icon-btn" title={expanded ? 'Collapse' : 'Expand'} onClick={onExpand}>
                {expanded ? <Minimize2 /> : <Maximize2 />}
              </button>
            )}
          </div>
        )}
      </div>
      {hasRows ? (
        <div className="rn-table-scroll" style={{ maxHeight: expanded ? undefined : 420 }}>
          <table className="rn-detail-table">
            <thead>
              <tr>
                <th>Property</th>
                {columns.map((col) => {
                  const isSorted = sort?.key === col.key;
                  return (
                    <th
                      key={col.key}
                      className="rn-align-right rn-sortable"
                      aria-sort={isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={() => onSort(col.key)}
                    >
                      {col.label}
                      {isSorted && <span className="rn-sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.id}>
                  <td className="rn-cell-strong">{row.name}</td>
                  {columns.map((col) => {
                    const cell = row.cells?.[col.key];
                    return (
                      <td key={col.key} className="rn-align-right rn-cell-mono" {...(cellHoverEvents(cell) || {})}>
                        {renderCell(cell, col)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {totalRow && (
              <tfoot>
                <tr>
                  <td>{totalRow.name || 'Total'}</td>
                  {columns.map((col) => {
                    const cell = totalRow.cells?.[col.key];
                    return (
                      <td key={col.key} className="rn-align-right rn-cell-mono" {...(cellHoverEvents(cell) || {})}>
                        {renderCell(cell, col)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (
        <div className="rn-empty-note">{emptyMessage}</div>
      )}
    </section>
  );
}

export default PerformanceSummary;
export { PerformanceSummary };
